'use client';

import Image from 'next/image';
import { useRef, Fragment } from 'react';
import { easeIn, easeInOut, easeOut, motion, useScroll, useTransform } from 'framer-motion';
import { PortableTextBlock } from 'sanity';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { useIsMobile } from '@/hooks/useIsMobile';
import ChipText from '@/components/global/ChipText';
import {GROQImage} from "@/sanity/lib/types";

interface IOutboundProps {
  chipText: string;
  title: PortableTextBlock[];
  items: {
    icon: GROQImage;
    title: string;
    description: PortableTextBlock[];
  }[];
}

export default function Outbond({
  chipText,
  title,
  items,
}: IOutboundProps) {
  const isMobile = useIsMobile();

  return (
    <section className="flex flex-col w-full items-center justify-center bg-[#072922] py-[12rem] lg:pt-[17.5rem] lg:pb-[19.75rem]">
      <div className="container grid grid-cols-1 lg:grid-cols-2 gap-[5rem] lg:gap-0 w-full relative">
        <div className="flex flex-col gap-[2rem] lg:pt-[2rem]">
          {chipText &&
            <ChipText text={chipText} color="green" />
          }
          <div className="richtext richtext-start flex flex-col gap-[3rem] prose-headings:text-offwhite prose-p:max-w-[67.375rem] prose-p:text-[2rem] lg:prose-p:text-[2.5rem] prose-p:font-[300] prose-p:text-[#F6F4E9]/60 lg:[&_br]:hidden">
            <PortableText value={title} components={richTextComponent} />
          </div>
        </div>
        
        <div className="flex flex-col lg:grid lg:grid-cols-2 lg:gap-x-[3rem] w-full lg:items-stretch">
          {items?.length > 0 && items?.map((item, index) => (
            <Fragment key={index}>
              <div className="flex flex-col">
                <motion.div
                  initial={{ opacity: 0, y: -50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: easeOut, delay: index * 0.15 }}
                  viewport={{ once: true }}
                  className="flex flex-col lg:flex-row self-stretch gap-[3rem] lg:gap-[4rem] bg-fakeblack/40 border border-[#F6F4E9]/[16%] lg:border-none rounded-[.5rem] px-[4rem] lg:px-[5rem] py-[5rem] lg:min-h-[31.5rem]"
                >
                  <div className="hidden lg:flex flex-col w-[8rem] h-[8rem] aspect-square items-center justify-center bg-green flex-shrink-0">
                    <Image
                      className="!w-[4rem] !h-[4rem]"
                      src={item.icon.src}
                      width={item.icon.width}
                      height={item.icon.height}
                      quality={100}
                      alt=""
                    />
                  </div>

                  {/* Mobile */}
                  <div className="flex flex-row items-center gap-[2rem] lg:hidden">
                    <div className="flex flex-col w-[5rem] h-[5rem] aspect-square items-center justify-center bg-green flex-shrink-0">
                      <Image
                        className="!w-[2.5rem] !h-[2.5rem]"
                        src={item.icon.src}
                        width={item.icon.width}
                        height={item.icon.height}
                        quality={100}
                        alt=""
                      />
                    </div>
                    <div className="richtext richtext-start flex flex-col gap-[3rem] prose-headings:text-offwhite prose-p:max-w-[67.375rem] prose-p:text-[#F6F4E9]/60">
                      <h3 className="text-[3rem] font-[400] text-offwhite leading-[120%] mb-0">{item.title}</h3>
                    </div>
                  </div>

                  {/* Desktop */}
                  <div className="richtext richtext-start flex flex-col gap-[1rem] prose-headings:text-offwhite prose-p:max-w-[67.375rem] prose-p:text-[#F6F4E9]/60">
                    <h3 className="text-[4rem] font-[400] text-offwhite leading-[120%] mb-0 hidden lg:block">{item.title}</h3>
                    <p className="text-[2rem] font-[300] lg:font-[400] text-[#F6F4E9]/50 leading-[140%] mb-0">
                      <PortableText value={item.description} components={richTextComponent} />
                    </p>
                  </div>
                </motion.div>

                {(isMobile ? index < items.length - 1 : (index === 0 || index === 1)) && (
                  <motion.svg
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.4, ease: easeOut, delay: index * 0.15 + 0.3 }}
                    viewport={{ once: true }}
                    className="ml-[5rem] h-[3rem] lg:h-auto"
                    xmlns="http://www.w3.org/2000/svg"
                    width="2"
                    height="56"
                    viewBox="0 0 2 56"
                    fill="none"
                  >
                    <path d="M1 0V56" stroke="url(#paint0_linear_873_2174)" strokeWidth="2"/>
                    <defs>
                      <linearGradient id="paint0_linear_873_2174" x1="1.5" y1="0" x2="1.5" y2="56" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#B0F4B7" stopOpacity="0.4"/>
                        <stop offset="1" stopColor="#B0F4B7" stopOpacity="0.1"/>
                      </linearGradient>
                    </defs>
                  </motion.svg>
                )}
              </div>
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}
